import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSession } from '../state/session';
import { getRooms, getHotel, searchHotels } from './hotels';
import { listMyBookings } from './bookings';
import { getSelfHostBooking, listHostBookings } from './payments';
import { getHostStatus } from './user';
import {
  getPayoutStatus,
  getProperty,
  listMembers,
  listMyProperties,
  listRoomImages,
  listRooms,
} from './selfhost';
import { getBlogPost, listBlogPosts } from './content';

const round = (n) => Math.round(Number(n) * 1e5) / 1e5;

export const queryKeys = {
  search: ({ date, lat, lng, cc }) => ['search', date, round(lat), round(lng), cc],
  hotel: (id, provider, date) => ['hotel', String(id), Number(provider), date],
  rooms: (id, provider, date, guests) => ['rooms', String(id), Number(provider), date, guests],
  blog: () => ['blog'],
  blogPost: (id) => ['blog', String(id)],
  myBookings: () => ['me', 'bookings'],
  selfHostBooking: (bookingNumber) => ['me', 'sh-booking', bookingNumber],
  hostStatus: () => ['me', 'host-status'],
  properties: () => ['me', 'properties'],
  property: (id) => ['me', 'property', String(id)],
  propertyRooms: (hotelId) => ['me', 'property', String(hotelId), 'rooms'],
  roomImages: (productId) => ['me', 'room-images', String(productId)],
  hostBookings: (params) => ['me', 'host-bookings', params],
  payout: () => ['me', 'payout'],
  members: (hotelId) => ['me', 'members', String(hotelId)],
};

const hotelId = (h) => `${h.provider ?? 0}:${h.hotel_id}`;

/**
 * Next page number while the last page brought hotels we hadn't seen;
 * stops on an empty page or a page of repeats.
 */
export function nextSearchPage(last, pages) {
  if (!last?.hotels?.length) return undefined;
  const seen = new Set(pages.slice(0, -1).flatMap((p) => (p?.hotels || []).map(hotelId)));
  return last.hotels.some((h) => !seen.has(hotelId(h))) ? pages.length + 1 : undefined;
}

/** Paged hotel search; pages are fetched as the user scrolls. */
export function useHotelSearch({ date, lat, lng, cc }, { enabled = true } = {}) {
  return useInfiniteQuery({
    queryKey: queryKeys.search({ date, lat, lng, cc }),
    queryFn: ({ pageParam }) => searchHotels({ date, page: pageParam, lat, lng, cc }),
    initialPageParam: 1,
    getNextPageParam: nextSearchPage,
    enabled: enabled && date != null && lat != null && lng != null,
    staleTime: 5 * 60 * 1000,
  });
}

/** First page of hotels near a point (landing page, "nearby" rails). */
export function useHotelsNear({ date, lat, lng, cc }, { enabled = true } = {}) {
  return useQuery({
    queryKey: [...queryKeys.search({ date, lat, lng, cc }), 'first'],
    queryFn: () => searchHotels({ date, page: 1, lat, lng, cc }),
    enabled: enabled && date != null && lat != null && lng != null,
    staleTime: 5 * 60 * 1000,
    select: (data) => data?.hotels || [],
  });
}

export function useHotel({ id, provider, date }) {
  return useQuery({
    queryKey: queryKeys.hotel(id, provider, date),
    queryFn: () => getHotel({ id, provider, date }),
    enabled: Boolean(id && date),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRooms({ id, provider, date, guests }) {
  return useQuery({
    queryKey: queryKeys.rooms(id, provider, date, guests),
    queryFn: () => getRooms({ id, provider, date, guests }),
    enabled: Boolean(id && date),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}

export function useMyBookings() {
  const { isSignedIn } = useSession();
  return useQuery({
    queryKey: queryKeys.myBookings(),
    queryFn: listMyBookings,
    enabled: isSignedIn,
  });
}

export function useSelfHostBooking(bookingNumber, { enabled = true } = {}) {
  const { isSignedIn } = useSession();
  return useQuery({
    queryKey: queryKeys.selfHostBooking(bookingNumber),
    queryFn: () => getSelfHostBooking(bookingNumber, { signedIn: isSignedIn }),
    enabled: enabled && Boolean(bookingNumber),
  });
}

export function useHostStatus() {
  const { isSignedIn } = useSession();
  return useQuery({
    queryKey: queryKeys.hostStatus(),
    queryFn: getHostStatus,
    enabled: isSignedIn,
    staleTime: 60 * 1000,
  });
}

export function useMyProperties({ enabled = true } = {}) {
  const { isSignedIn } = useSession();
  return useQuery({
    queryKey: queryKeys.properties(),
    queryFn: listMyProperties,
    enabled: isSignedIn && enabled,
  });
}

export function useProperty(id) {
  return useQuery({
    queryKey: queryKeys.property(id),
    queryFn: () => getProperty(id),
    enabled: Boolean(id),
  });
}

export function usePropertyRooms(hotelId) {
  return useQuery({
    queryKey: queryKeys.propertyRooms(hotelId),
    queryFn: () => listRooms(hotelId),
    enabled: Boolean(hotelId),
  });
}

export function useRoomImages(productId) {
  return useQuery({
    queryKey: queryKeys.roomImages(productId),
    queryFn: () => listRoomImages(productId),
    enabled: Boolean(productId),
  });
}

export function useHostBookings(params, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.hostBookings(params),
    queryFn: () => listHostBookings(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePayoutStatus({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.payout(),
    queryFn: getPayoutStatus,
    enabled,
    retry: false,
  });
}

export function useMembers(hotelId, { enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.members(hotelId),
    queryFn: () => listMembers(hotelId),
    enabled: enabled && Boolean(hotelId),
  });
}

export function useBlogPosts() {
  return useQuery({ queryKey: queryKeys.blog(), queryFn: listBlogPosts, staleTime: 10 * 60 * 1000 });
}

export function useBlogPost(id) {
  return useQuery({
    queryKey: queryKeys.blogPost(id),
    queryFn: () => getBlogPost(id),
    enabled: Boolean(id),
    staleTime: 10 * 60 * 1000,
  });
}
